import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Toggle } from "rsuite";
import dayjs from "dayjs";
import {
  usePropertyStore,
  useIsEditableRateplan,
} from "../../contexts/PropertyStoreContext";
import ConfirmModal from "../confirm-modal/ConfirmModal";
import LoaderModal from "../loader-modal/LoaderModal";
import { updateARI } from "../../api";
import { UAL_SOURCES } from "../../constants/ualActions";
import {
  selectSelectedRateplan,
  selectSelectedRateplanCurrency,
} from "../../slices/rateplanSlice";
import { formatCurrency } from "../../constants/utils";
import { useAriContext } from "../../contexts/AriContext";
import { selectQuickViewDateRange } from "../../slices/ariDateRangeSlice";
import {
  selectRoomtypes,
  selectSelectedRoomtype,
} from "../../slices/roomtypeSlice";

const DATE_FORMAT = "ddd D";

const getOpenLabel = (isOpen) => {
  return isOpen ? "Open" : "Closed";
};

const InventoryStatus = ({ inventory, isOpen }) => {
  const formatInventoryStatus = (inventory, isOpen) => {
    if (!inventory) {
      return inventory === 0 ? "Sold out!" : "";
    }
    return getOpenLabel(isOpen);
  };
  return <span>{formatInventoryStatus(inventory, isOpen)}</span>;
};

const DateCell = ({
  isPastDay,
  date,
  isSelected,
  onDateClick,
  isCurrentMonth,
  isGreaterThanTwoYear,
  ariData,
  dayDiff,
}) => {
  const currency = useSelector(selectSelectedRateplanCurrency);
  const { price, inventory, isOpen, minLeadTime } = ariData || {};
  const isRestricted = !isPastDay && dayDiff < minLeadTime;
  const rate =
    price === null || price === undefined
      ? "N/A"
      : formatCurrency(price, currency);
  let inventoryText = inventory ?? 0;
  if (isRestricted) {
    inventoryText = `Min. LT - ${minLeadTime} days`;
  }
  const isDisabled = isPastDay || isGreaterThanTwoYear || !isCurrentMonth;

  const [isOpened, setIsOpened] = useState(isOpen);
  const [showModal, setShowModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const { refetchAriData } = useAriContext();
  const { selectedProperty } = usePropertyStore();
  const selectedRateplan = useSelector(selectSelectedRateplan);
  const isEditable = useIsEditableRateplan();
  const currentMonth = useSelector(selectQuickViewDateRange);
  const rooms = useSelector(selectRoomtypes);
  const selectedRoomtype = useSelector(selectSelectedRoomtype);

  useEffect(() => {
    setIsOpened(isOpen);
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      handleDateClick();
    }
  };

  const handleDateClick = () => {
    if (isCurrentMonth && !isDisabled) {
      onDateClick({ date, ariData });
    }
  };

  const handleToggleOpen = (_, event) => {
    event.stopPropagation();
    if (!isDisabled && !isRestricted) {
      setShowModal(true);
      setConfirmationMessage(
        `You’re about to ${isOpen ? "close" : "open"} availability for ${date.format("ddd Do MMMM")}. Are you happy to continue?`
      );
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleConfirm = () => {
    setShowModal(false);
    setShowLoader(true);

    // Picking rateplanId, roomtypeId and propertyId from selected value in as ARI data may be missing when not set
    const payload = {
      propertyId: ariData?.propertyId || selectedProperty?.propertyId,
      dateRange: {
        startDate: date.format("YYYY-MM-DD"),
        endDate: date.format("YYYY-MM-DD"),
      },
      productUpdates: [
        {
          rateplanId: ariData.rateplanId || selectedRateplan?.value,
          roomtypeId: ariData.roomtypeId || selectedRoomtype,
          updates: [{ ...ariData, isOpen: !isOpened }],
        },
      ],
    };

    updateARI(payload, UAL_SOURCES.ARI_CALENDAR_INDIVIDUAL)
      .then(() => {
        setTimeout(() => {
          const startDate = currentMonth.startOf("month").format("YYYY-MM-DD");
          const endDate = currentMonth.endOf("month").format("YYYY-MM-DD");
          refetchAriData({
            propertyId: selectedProperty?.propertyId,
            rateplanId: ariData.rateplanId || selectedRateplan?.value,
            rooms,
            startDate,
            endDate,
            includeNumberOfRoomsSold: true,
            roomtypeIdsToRefresh: [payload.productUpdates[0].roomtypeId],
          });
          toast.success("Successfully updated.");
          onDateClick({ date, ariData: { ...ariData, isOpen: !isOpened } });
          setShowLoader(false);
          setIsOpened(!isOpened);
        }, 2000);
      })
      .catch((error) => {
        setShowLoader(false);
        toast.error(getApiErrorMessage(error, "Failed to save updates."));
      });
  };

  const status = useMemo(() => {
    if (isRestricted) {
      return "limited";
    }
    if (isDisabled) {
      return "disabled";
    }
    if (!isOpened) {
      return "closed";
    }
    if (!inventory) {
      return "unavailable";
    }
    return "available";
  }, [isDisabled, isRestricted, inventory, isOpened]);

  const displayDate = date.format(DATE_FORMAT);

  return (
    <div
      className={classNames("col cell", status, {
        selected: !isDisabled && isSelected,
        hidden: !isCurrentMonth,
      })}
      onClick={handleDateClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="text-content">
        <span className="date">{displayDate}</span>
        <div className="available">
          <span
            className={classNames("badge rounded-circle", {
              "d-none": isPastDay || isGreaterThanTwoYear,
            })}
          >
            <span className="visually-hidden">{status}</span>
          </span>
          <span className="inventory">{ariData ? inventoryText : ""}</span>
        </div>
        <div className="rate">{rate}</div>
      </div>
      <div className="toggle-wrapper">
        {ariData &&
          (isEditable ? (
            <Toggle
              size="sm"
              checked={isOpened}
              onChange={handleToggleOpen}
              disabled={isDisabled || isRestricted}
              locale={{ on: "Open", off: "Closed" }}
            >
              {getOpenLabel(isOpen)}
            </Toggle>
          ) : (
            <InventoryStatus inventory={inventory} isOpen={isOpened} />
          ))}
      </div>
      <ConfirmModal
        show={showModal}
        onClose={handleClose}
        onConfirm={handleConfirm}
        message={confirmationMessage}
        title="Please confirm update"
      />
      <LoaderModal showLoader={showLoader} setShowLoader={setShowLoader} />
    </div>
  );
};

DateCell.propTypes = {
  isPastDay: PropTypes.bool.isRequired,
  date: PropTypes.instanceOf(dayjs).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onDateClick: PropTypes.func.isRequired,
  isCurrentMonth: PropTypes.bool.isRequired,
  isGreaterThanTwoYear: PropTypes.bool.isRequired,
  ariData: PropTypes.shape({
    price: PropTypes.number,
    inventory: PropTypes.number,
    isOpen: PropTypes.bool,
    minLeadTime: PropTypes.number,
    propertyId: PropTypes.number.isRequired,
    rateplanId: PropTypes.number.isRequired,
    roomtypeId: PropTypes.number.isRequired,
  }),
  dayDiff: PropTypes.number,
};

InventoryStatus.propTypes = {
  inventory: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
};

export default DateCell;
